import React, { PureComponent } from 'react'
import moment from 'moment'
import styled from 'styled-components'
import BigCalendar from 'react-big-calendar'
import { media, Icon } from 'gipsy-ui'
import { utils, styles } from 'gipsy-misc'
import { calendar } from 'gipsy-api'
import variables from 'assets/styles/variables'

import WeekLabel from 'features/weekLabel'
import Calendar from 'features/calendar'
import container from './container'
import TaskPanel from 'features/taskPanel'
import Circle from 'features/circle'
import { getCurrentPathName } from 'utils/location'

class CalendarPage extends PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      loading: false,
      calendarItems: [],
      firstWeekDay: moment()
        .weekday(0)
        .format('YYYY-MM-DD'),
      lastWeekDay: moment()
        .weekday(6)
        .format('YYYY-MM-DD'),
      workLoad: [],
      date: new Date(),
      calendarDate: new Date(),
      view: this.props.match.params.view ? this.props.match.params.view : BigCalendar.Views.DAY,
      scrollToTime: this.getScrollTimeForToday()
    }
  }

  componentDidMount() {
    this.fetch()
    this.fetchWorkload()
    this.startInterval()
  }

  componentWillUnmount() {
    this.clearInterval()
  }

  startInterval = () => {
    this.intervalRefresh = setInterval(this.fetch, 60 * 1000)
  }

  clearInterval = () => {
    clearInterval(this.intervalRefresh)
    this.intervalRefresh = undefined
  }

  getScrollTimeForToday = () => {
    const scrollToTime = new Date()
    scrollToTime.setHours(scrollToTime.getHours() - 1)

    return scrollToTime
  }

  fetchWorkload = async (date = this.state.date) => {
    calendar.cancel('getWeeklyWorkload')
    const formattedDate = moment(date).format('YYYY-MM-DD')
    try {
      const result = await calendar.getWeeklyWorkload(formattedDate)
      const firstWeekDay = result[0].date
      const lastWeekDay = result[result.length - 1].date
      this.setState({ workLoad: result, firstWeekDay, lastWeekDay })
    } catch (err) {
      console.error(err)
    }
  }

  fetch = async () => {
    if (this.state.view === BigCalendar.Views.DAY) {
      this.fetchForDayView(this.state.date)
    } else {
      this.fetchForWeekView(this.state.firstWeekDay, this.state.lastWeekDay)
    }
  }

  fetchForWeekView = async (firstWeekDay, lastWeekDay) => {
    calendar.cancel('getRange')
    this.setState({ firstWeekDay, lastWeekDay })
    const isFetchingSameDate =
      utils.datetime.isSameDate(this.state.firstWeekDay, firstWeekDay) &&
      utils.datetime.isSameDate(this.state.lastWeekDay, lastWeekDay)
    if (!isFetchingSameDate) {
      this.setState({ loading: true, firstWeekDay, lastWeekDay })
    }
    let calendarItems = await calendar.getRange(firstWeekDay, lastWeekDay)

    calendarItems = calendarItems.map((calendarItem, index) => {
      const { allDay, start, end } = this.computeInfoFromCalendarItem(calendarItem)

      return {
        ...calendarItem,
        index,
        allDay,
        start,
        end
      }
    })
    const nextState = {
      calendarItems,
      firstWeekDay,
      lastWeekDay,
      calendarDate: utils.datetime.toDate(firstWeekDay),
      loading: false
    }
    if (isFetchingSameDate) {
      this.setState(nextState)
      return
    }

    this.setState({ calendarItems: [] }, () => {
      /* fix animation bug : calendar event components were reused instead of recreated */
      this.setState(nextState)
    })
  }

  fetchForDayView = async date => {
    calendar.cancel('get')
    this.setState({ date })
    const isFetchingSameDate = utils.datetime.isSameDate(this.state.date, date)
    if (!isFetchingSameDate) {
      this.setState({ loading: true })
    }
    const formattedDate = moment(date).format('YYYY-MM-DD')
    let calendarItems = await calendar.get(formattedDate)
    let scrollToTime = new Date(date)
    if (utils.datetime.isToday(date)) {
      scrollToTime = new Date()
    } else {
      scrollToTime.setHours(23, 59, 59, 999)
    }
    calendarItems = calendarItems.map((calendarItem, index) => {
      const { allDay, start, end } = this.computeInfoFromCalendarItem(calendarItem)

      /* if day is not today, we scroll to the first calendarItem */
      if (!utils.datetime.isToday(date) && !allDay && start < scrollToTime) {
        scrollToTime = new Date(start)
      }
      return {
        ...calendarItem,
        index,
        allDay,
        start,
        end
      }
    })
    const nextState = { calendarItems, date, calendarDate: date, loading: false }
    if (isFetchingSameDate) {
      this.setState(nextState)
      return
    }
    scrollToTime.setHours(scrollToTime.getHours() - 1)
    nextState.scrollToTime = scrollToTime
    this.setState({ calendarItems: [] }, () => {
      /* fix animation bug : calendar event components were reused instead of recreated */
      this.setState(nextState)
    })
  }

  computeInfoFromCalendarItem = calendarItem => {
    const allDay = utils.event.isAllDayEvent(calendarItem.item)
    const estimatedTime = utils.datetime.convertNanosecondsToMinute(calendarItem.item.estimatedTime)
    const start = new Date(calendarItem.start)

    let end = new Date(calendarItem.end)
    if (estimatedTime < 15) {
      end = new Date(start)
      end.setMinutes(end.getMinutes() + 15)
    }
    if (calendarItem.item.free) {
      end = new Date(start)
      end.setMinutes(end.getMinutes() + utils.datetime.convertNanosecondsToMinute(calendarItem.item.estimatedTime))
    }
    return { allDay, start, end }
  }

  previousWeekForWeekview = () => {
    const firstWeekDay = utils.datetime.addToDate(this.state.firstWeekDay, -7)
    const lastWeekDay = utils.datetime.addToDate(this.state.lastWeekDay, -7)
    this.onChangeWeek(firstWeekDay, lastWeekDay)
  }

  nextWeekForWeekview = () => {
    const firstWeekDay = utils.datetime.addToDate(this.state.firstWeekDay, 7)
    const lastWeekDay = utils.datetime.addToDate(this.state.lastWeekDay, 7)
    this.onChangeWeek(firstWeekDay, lastWeekDay)
  }

  previousWeek = () => {
    const date = new Date(this.state.date)
    const weekday = moment(date).weekday()
    date.setDate(date.getDate() - 6 - weekday)
    this.onChangeDate(date)
  }

  nextWeek = () => {
    const date = new Date(this.state.date)
    const weekday = moment(date).weekday()
    date.setDate(date.getDate() + 8 - weekday)
    this.onChangeDate(date)
  }

  onChangeDate = (date, view, action) => {
    this.clearInterval()
    this.startInterval()
    date = new Date(date)
    this.fetchForDayView(date)

    if (
      utils.datetime.isBefore(date, this.state.firstWeekDay) ||
      utils.datetime.isAfter(date, this.state.lastWeekDay)
    ) {
      this.fetchWorkload(date)
    }
  }

  onChangeWeek = (firstWeekDay, lastWeekDay) => {
    this.clearInterval()
    this.startInterval()
    this.fetchForWeekView(firstWeekDay, lastWeekDay)
  }

  onChangeView = view => {
    this.setState({ view })
  }

  onSelectCalendarItem = async ({ item }) => {
    switch (item.type) {
      case 'event':
        if (!item.webLink) {
          return
        }
        window.open(item.webLink)
        break
      case 'task':
        await this.props.actions.openPanel(item.id)
        await this.props.actions.markTaskAsRead(item.id)
        break
      default:
        return
    }
  }

  onSelectSlot = ({ start, end }) => {
    const startTime = moment(start)
    const endTime = moment(end)
    const diff = endTime.diff(startTime, 'minutes')
    const estimatedTime = utils.datetime.convertMinuteToNanoseconds(diff)
    const initialTask = { pin: { time: moment(start).format() }, estimatedTime, shouldBlockInCalendar: true }
    this.props.actions.openPanel(null, initialTask)
  }

  onCompleteTask = async ({ id, paramName, value }) => {
    const { calendarItems } = this.state
    const index = utils.array.getObjectIndexInArrayByPath(calendarItems, 'item.id', id)
    if (index !== -1) {
      const newCalendarItems = utils.array.setValueInArrayAtIndexByPath(calendarItems, index, 'item.completed', value)
      this.setState({ calendarItems: newCalendarItems })
    }
    await this.props.actions.toggleCompleteTaskRequest(id, value, { pageSource: getCurrentPathName() })
    utils.resolve.once('fetch', this.fetch)
  }

  onCreateTaskPanel = task => {
    utils.resolve.once('fetch', this.fetch)
  }

  onChangeTaskPanel = task => {
    utils.resolve.once('fetch', this.fetch)
  }

  onDeleteTaskPanel = task => {
    utils.resolve.once('fetch', this.fetch)
  }

  renderHeader = () => {
    switch (this.state.view) {
      case BigCalendar.Views.DAY:
        return (
          <Title className="title" absolute={true}>
            <h1>{utils.datetime.humanizeDate(this.state.date)}</h1>
          </Title>
        )
      case BigCalendar.Views.WEEK:
        return (
          <WeekLabel
            firstDate={this.state.firstWeekDay}
            lastDate={this.state.lastWeekDay}
            onClickLeft={this.previousWeekForWeekview}
            onClickRight={this.nextWeekForWeekview}
          />
        )
      default:
        return null
    }
  }

  renderAside = () => {
    if (this.state.view !== BigCalendar.Views.DAY) {
      return null
    }

    const currentDate = this.state.date
    const defaultColors = [styles.colors.lightGrey, styles.colors.darkGrey]

    return (
      <Aside className="aside">
        <Title>
          <WeekLabel
            firstDate={this.state.firstWeekDay}
            lastDate={this.state.lastWeekDay}
            onClickLeft={this.previousWeek}
            onClickRight={this.nextWeek}
          />
        </Title>
        <AsideContent>
          {this.state.workLoad.map(w => {
            const date = utils.datetime.toDate(w.date)
            const dateNumber = date.getDate()
            const percent = (w.workScheduledRatio || w.workCompletedRatio) * 100 || 0
            const colors = [].concat(defaultColors)
            if (percent > 100 && utils.datetime.isSameDate(currentDate, date)) {
              colors[1] = styles.colors.pinkColor
            }
            const values = [percent, percent - 100].map(v => (v < 0 ? 0 : v > 100 ? 100 : v))
            return (
              <Row
                key={w.date}
                active={utils.datetime.isSameDate(currentDate, date)}
                onClick={() => this.onChangeDate(date)}>
                <Circle strokeWidth={35} size={32} values={values} colors={colors}>
                  <CircleText>{dateNumber}</CircleText>
                </Circle>
                <CellDay>{utils.datetime.getShortWeekDay(date)}</CellDay>
                <CountCell>
                  <Icon icon="Calendar" size={12} />
                  <CountCellValue>{w.numEvents}</CountCellValue>
                </CountCell>
                <CountCell>
                  <BackgroundIcon
                    className="background-icon"
                    active={utils.datetime.isSameDate(currentDate, date)}
                    icon="Checked"
                    fill="white"
                    size={12}
                  />
                  <CountCellValue>{w.numTasks}</CountCellValue>
                </CountCell>
              </Row>
            )
          })}
        </AsideContent>
      </Aside>
    )
  }

  render() {
    return (
      <Container className="calendar-page">
        <Content className="content">
          {this.renderHeader()}
          <StyledCalendar
            showHeader={this.state.view === BigCalendar.Views.DAY}
            loading={this.state.loading}
            calendarItems={this.state.calendarItems}
            date={this.state.calendarDate}
            scrollToTime={this.state.scrollToTime}
            onChangeDate={this.onChangeDate}
            onSelectCalendarItem={this.onSelectCalendarItem}
            view={this.state.view}
            onSelectSlot={this.onSelectSlot}
            onChangeView={this.onChangeView}
            onCompleteTask={this.onCompleteTask}
            weeklyWorkingHours={this.props.weeklyWorkingHours}
          />
        </Content>
        {this.renderAside()}
        <TaskPanel
          onCreate={this.onCreateTaskPanel}
          onChange={this.onChangeTaskPanel}
          onDelete={this.onDeleteTaskPanel}
        />
      </Container>
    )
  }
}

export default container(CalendarPage)

const HEADER_HEIGHT = 60

const Container = styled.div`
  margin: auto;
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  ${media['phone-wide']`
    padding-left: 20px;
    padding-right: 20px;
  `}
`

const CircleText = styled.div`
  font-size: 12px;
`

const Content = styled.div`
  position: relative;
  margin: auto;
  height: 100%;
  max-width: ${variables.pageMaxWidth};
  flex-grow: 1;
  flex-shrink: 1;
`

const StyledCalendar = styled(Calendar)`
  padding-top: ${props => (props.showHeader ? `${HEADER_HEIGHT}px` : '0px')};
  transition: opacity 0.3s ease-in-out;
  opacity: ${props => (props.loading ? 0.4 : 1)};
`

const Aside = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  width: 200px;
  height: 100%;
  padding: 0px 10px;
  font-size: 14px;
  margin-left: 10px;
  ${media['tablet']`
    display: none;
  `}
`

const AsideContent = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`

const Row = styled.div`
  padding: 10px 10px;
  margin: 0px -10px;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background-color: ${props => (props.active ? 'white' : 'transparent')};
  color: ${props => (props.active ? styles.colors.textDarkColor : styles.colors.darkGrey)};
  svg,
  g,
  path {
    fill: ${props => (props.active ? styles.colors.textDarkColor : styles.colors.darkGrey)};
  }
  &:hover {
    background-color: white;
  }
`

const BackgroundIcon = styled(Icon)`
  && {
    background-color: ${props => (props.active ? styles.colors.textDarkColor : styles.colors.darkGrey)};
    padding: 2px;
    border-radius: 4px;
    svg,
    g,
    path {
      fill: white;
    }
  }
`

const CellDay = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
  width: 100%;
  padding-left: 12px;
  font-size: 14px;
`

const CountCell = styled.div`
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  padding-left: 4px;
  font-size: 11px;
  justify-content: center;
  align-items: center;
  line-height: 12px;
`

const CountCellValue = styled.div`
  margin-left: 6px;
  width: 15px;
`

const Title = styled.div`
  position: ${props => (props.absolute ? 'absolute' : 'relative')};
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
  align-items: center;
  height: ${HEADER_HEIGHT}px;
  color: ${styles.colors.textDarkColor};
  font-weight: 500;
`



// WEBPACK FOOTER //
// ./src/pages/calendar/index.js